"use client";
import { useTranslations } from "next-intl";

import { useState, useMemo, useCallback, useEffect } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, X, AlertTriangle, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { SECTION_CONTAINER, SECTION_HEADING, SECTION_PADDING } from "@/lib/home-section-layout";

// Centralized and validated configuration
const CLOUDINARY_CONFIG = {
  cloudName: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME,
  videoId: process.env.NEXT_PUBLIC_CLOUDINARY_VIDEO_ID,
  profile: process.env.NEXT_PUBLIC_CLOUDINARY_PROFILE || "cld-default",
} as const;

// Configuration validation
const isCloudinaryConfigValid = () => {
  return CLOUDINARY_CONFIG.cloudName && CLOUDINARY_CONFIG.videoId;
};

// Enhanced CloudinaryPlayer with validation and error handling
const CloudinaryPlayer = () => {
  const t = useTranslations('gallery');

  // Build URL only if configuration is valid
  const embedUrl = useMemo(() => {
    if (!isCloudinaryConfigValid()) {
      console.error('Cloudinary configuration missing. Please check your environment variables.');
      return null;
    }

    const params = new URLSearchParams({
      cloud_name: CLOUDINARY_CONFIG.cloudName!,
      public_id: CLOUDINARY_CONFIG.videoId!,
      profile: CLOUDINARY_CONFIG.profile,
      fluid: 'true',
      controls: 'true',
      // Always muted. The player mounts only after a deliberate poster click,
      // and a click is not consent to sound — an unexpectedly loud autoplay is
      // the fastest way to lose the visitor who just chose to watch.
      muted: 'true',
      autoplay: 'true',
      loop: 'false',
      playsinline: 'true',
    });

    return `https://player.cloudinary.com/embed/?${params.toString()}`;
  }, []);

  // Show fallback if no valid configuration. The diagnosis stays in the console
  // above; what renders here is read by customers, so it says what they can do
  // about it rather than naming an environment variable they cannot set.
  if (!embedUrl) {
    return (
      <div className="relative w-full h-full bg-gray-800 flex items-center justify-center rounded-3xl">
        <div className="text-center text-white p-4">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-yellow-500" aria-hidden="true" />
          <p className="text-sm">{t('videoUnavailable')}</p>
          <p className="text-xs text-gray-400 mt-1">{t('videoUnavailableHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full">
      <iframe
        src={embedUrl}
        className="absolute inset-0 w-full h-full rounded-3xl"
        allow="autoplay; encrypted-media; fullscreen"
        allowFullScreen
        title="Miller 40 Shop Video"
        frameBorder="0"
        loading="lazy"
        onError={() => {
          console.error('Failed to load Cloudinary video player');
        }}
      />
    </div>
  );
};

// Move images outside component to prevent re-creation on each render
const DEFAULT_IMAGES: GalleryImage[] = [
  {
    id: "exterior-sunset",
    src: "/gallery/shopsunset.png",
    alt: "Miller40 shop exterior at sunset",
    width: 800,
    height: 500,
  },
  {
    id: "service-bays",
    src: "/gallery/station.png",
    alt: "Service bays with cars being worked on",
    width: 600,
    height: 400,
  },
  {
    id: "miller40-team",
    src: "/gallery/miller40-team.jpg",
    alt: "Miller40 team in front of the shop",
    width: 300,
    height: 200,
  },
  {
    id: "staff-photo",
    src: "/gallery/staff1.png",
    alt: "Mechanic working on a tire",
    width: 300,
    height: 200,
  },
  {
    id: "car-center",
    src: "/gallery/car-center.jpg",
    alt: "The car center with multiple vehicles",
    width: 300,
    height: 200,
  },
  {
    id: "pet-friendly",
    src: "/gallery/petfriendly.jpg",
    alt: "Pet friendly admin",
    width: 600,
    height: 400,
  },
  {
    id: "team-miami",
    src: "/gallery/team-mechanic-shop.jpg",
    alt: "Team miami",
    width: 600,
    height: 400,
  },
  {
    id: "team",
    src: "/gallery/team1.jpg",
    alt: "Our team at work",
    width: 600,
    height: 400,
  },
  {
    id: "our-car",
    src: "/gallery/our-car.jpg",
    alt: "Our car parked in front of the shop", 
    width: 600,
    height: 400,
  },
  {
    id: "view-part",
    src: "/gallery/view-part.jpg",
    alt: "View of auto parts in the shop",
    width: 600,
    height: 400,
  },
  {
    id: "machine",
    src: "/gallery/machine.jpg",
    alt: "Automotive service machine in the workshop",
    width: 600,
    height: 400,
  },
  {
    id: "tire-balance-machine",
    src: "/gallery/tire-balance-machine.png",
    alt: "Automotive service machine balance wheels in the workshop",
    width: 600,
    height: 400,
  },
] as const;

// Define the image type that will come from the backend
interface GalleryImage {
  id: string;
  src: string;
  alt: string;
  width: number;
  height: number;
}

interface GallerySectionProps {
  className?: string;
  sectionTitle?: string;
  sectionSubtitle?: string;
  images?: GalleryImage[];
}

export function GallerySection({
  className,
  sectionTitle: _sectionTitle,
  sectionSubtitle: _sectionSubtitle,
  images,
}: GallerySectionProps) {
  const t = useTranslations('gallery');
  const isMobileLandscape = useIsMobileLandscape();
  
  // Use useMemo to prevent unnecessary recalculations
  const galleryImages = useMemo(() => images || DEFAULT_IMAGES, [images]);
  
  // State for the lightbox modal
  const [isOpen, setIsOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isVideoActive, setIsVideoActive] = useState(false);

  // Use useMemo for derived calculations
  const visibleImages = useMemo(() => galleryImages.slice(0, 5), [galleryImages]);
  // Four, not five: the grid's first slot is the video, so it shows
  // galleryImages[1..4] and never [0]. Subtracting 5 understated the hidden
  // count by one and made the "+N" badge disagree with the lightbox.
  const remainingCount = useMemo(() => Math.max(0, galleryImages.length - 4), [galleryImages.length]);

  // Use useCallback for functions passed as props
  const goToNextImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1));
  }, [galleryImages.length]);

  const goToPrevImage = useCallback(() => {
    setCurrentImageIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1));
  }, [galleryImages.length]);

  const openLightbox = useCallback((index: number) => {
    setCurrentImageIndex(index);
    setIsOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Keyboard support for modal
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          closeLightbox();
          break;
        case 'ArrowLeft':
          goToPrevImage();
          break;
        case 'ArrowRight':
          goToNextImage();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden'; // Prevent body scroll

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeLightbox, goToPrevImage, goToNextImage]);

  return (
    <section 
      id="gallery" 
      data-surface="base"
      className={cn(SECTION_PADDING, "font-outfit bg-site", className)}
      aria-label="Photo gallery showcasing Miller40 workshop"
    >
      <div className={cn(SECTION_CONTAINER, isMobileLandscape && "!mx-6")}>
        <div className="text-center mb-10">
          <h2 className={SECTION_HEADING}>
            {t('sectionTitle')}
          </h2>
          <p className="text-white">
            {t('sectionSubtitle')}
          </p>
        </div>

        {/* Gallery Grid Layout */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {/* Large left video */}
          <div className="md:col-span-6 relative">
            <div className="relative h-[240px] md:h-[400px] border rounded-3xl overflow-hidden">
              {isVideoActive ? (
                <CloudinaryPlayer />
              ) : (
                <button
                  type="button"
                  onClick={() => setIsVideoActive(true)}
                  className="group relative h-full w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500"
                >
                  <Image
                    src="/gallery/team-mechanic-shop.jpg"
                    alt=""
                    fill
                    sizes="(max-width: 768px) 100vw, min(50vw, 880px)"
                    className="object-cover"
                  />
                  <span className="absolute inset-0 bg-black/45 transition-colors group-hover:bg-black/35" />
                  <span className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
                    <span className="grid h-16 w-16 place-items-center rounded-full bg-yellow-500 text-black transition-transform group-hover:scale-105">
                      <Play className="ml-1 h-7 w-7 fill-current" aria-hidden="true" />
                    </span>
                    <span className="text-sm font-semibold">
                      {t('videoWatchLabel')} · 0:31
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Right column with grid */}
          <div className="md:col-span-6 grid grid-cols-1 md:grid-rows-4 gap-4">
            {/* Top large image */}
            <div className="hidden md:block md:row-span-2 relative">
              <div
                className="relative h-[110px] md:h-full rounded-3xl overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-500"
                onClick={() => openLightbox(1)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openLightbox(1);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Open ${visibleImages[1]?.alt} in lightbox`}
              >
                <Image
                  src={visibleImages[1]?.src || "/placeholder.svg"}
                  alt={visibleImages[1]?.alt || "Gallery image"}
                  fill
                  sizes="(max-width: 768px) 100vw, min(50vw, 880px)"
                  className="object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* Bottom row of 3 smaller images */}
            <div className="md:row-span-2 grid grid-cols-3 gap-4">
              {visibleImages.slice(2, 5).map((image, index) => (
                <div
                  key={image.id}
                  className="relative h-[190px] rounded-3xl overflow-hidden cursor-pointer group focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  onClick={() => openLightbox(index + 2)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openLightbox(index + 2);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${image.alt} in lightbox`}
                >
                  <Image
                    src={image.src || "/placeholder.svg"}
                    alt={image.alt}
                    fill
                    sizes="(max-width: 768px) 33vw, min(16vw, 300px)"
                    className="object-cover hover:scale-105 transition-transform duration-500"
                  />
                  {index === 2 && remainingCount > 0 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/70 transition-colors">
                      <span className="text-yellow-500 text-xl font-bold">
                        +{remainingCount}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Lightbox Modal */}
        {isOpen && (
          <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
            <div className="relative w-full h-full max-w-6xl max-h-[90vh] mx-auto flex items-center justify-center">
              {/* Close button with better accessibility */}
              <button
                onClick={closeLightbox}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    closeLightbox();
                  }
                }}
                className="absolute top-4 right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                aria-label="Close gallery"
              >
                <X className="h-6 w-6" />
              </button>

              {/* Navigation buttons with keyboard support */}
              <button
                onClick={goToPrevImage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToPrevImage();
                  }
                }}
                className="absolute left-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                onClick={goToNextImage}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToNextImage();
                  }
                }}
                className="absolute right-4 z-10 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                aria-label="Next image"
              >
                <ChevronRight className="h-6 w-6" />
              </button>

              {/* Current media */}
              <div className="relative w-full h-full p-8 flex items-center justify-center">
                {/* Every slide is an image. The video is activated in place on
                    the grid and never opens the lightbox, so reserving index 0
                    for it made galleryImages[0] unreachable and the counter
                    below ("1 / N") count a slide that N excluded. */}
                <Image
                  src={galleryImages[currentImageIndex]?.src || "/placeholder.svg"}
                  alt={galleryImages[currentImageIndex]?.alt || "Gallery image"}
                  fill
                  className="object-contain"
                  priority
                />
              </div>

              {/* Enhanced counter */}
              <div className="absolute bottom-4 left-0 right-0 text-center text-white">
                <span className="bg-black/50 px-3 py-1 rounded-full text-sm">
                  {currentImageIndex + 1} / {galleryImages.length}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}